import React, { useState, useEffect } from 'react'
import { Input, Select, Button, Space, Divider, message } from 'antd'
import CustomSimpleTable, { StatusPill } from '@/components/UI/Tables/CustomSimpleTable'
import CustomModal from '@/components/UI/Modals/CustomModal'
import { FilterDropdown } from '@/components/UI/Filter'
import { useGetDriversQuery, useAddDriverMutation, useUpdateDriverMutation, useToggleDriverStatusMutation, useDeleteDriverMutation } from '@/services/api/driver/driverApi'
import { useGetVehiclesQuery, useAddVehicleMutation, useUpdateVehicleMutation, useDeleteVehicleMutation } from '@/services/api/vehicle/vehicleApi'
import { useGetLocationsQuery } from '@/services/api/location/locationApi'
import ConfirmModal from '@/components/UI/Modals/ConfirmModal'

const Drivers = () => {
  // API hooks
  const { data: locationsData, isLoading: locationsLoading } = useGetLocationsQuery()
  
  const { data: driversData, isLoading: driversLoading, error: driversError } = useGetDriversQuery()
  const { data: vehiclesData, isLoading: vehiclesLoading, error: vehiclesError } = useGetVehiclesQuery()
  const [addDriver, { isLoading: addingDriver }] = useAddDriverMutation()
  const [updateDriver, { isLoading: updatingDriver }] = useUpdateDriverMutation()
  const [deleteDriver, { isLoading: deletingDriver }] = useDeleteDriverMutation()
  const [toggleDriverStatus, { isLoading: togglingDriverStatus }] = useToggleDriverStatusMutation()
  const [addVehicle, { isLoading: addingVehicle }] = useAddVehicleMutation()
  const [updateVehicle, { isLoading: updatingVehicle }] = useUpdateVehicleMutation()
  const [deleteVehicle, { isLoading: deletingVehicle }] = useDeleteVehicleMutation()


  // State management
  const [selectedDrivers, setSelectedDrivers] = useState([])
  const [selectedVehicles, setSelectedVehicles] = useState([])
  const [showAddDriverModal, setShowAddDriverModal] = useState(false)
  const [showEditDriverModal, setShowEditDriverModal] = useState(false)
  const [showViewDriverModal, setShowViewDriverModal] = useState(false)
  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false)
  const [showEditVehicleModal, setShowEditVehicleModal] = useState(false)
  const [showViewVehicleModal, setShowViewVehicleModal] = useState(false)
  const [editingDriver, setEditingDriver] = useState(null)
  const [editingVehicle, setEditingVehicle] = useState(null)
  const [viewingDriver, setViewingDriver] = useState(null)
  const [viewingVehicle, setViewingVehicle] = useState(null)
  const [filteredDrivers, setFilteredDrivers] = useState([])
  const [filteredVehicles, setFilteredVehicles] = useState([])
  const [filters, setFilters] = useState({})
  const [vehicleFilters, setVehicleFilters] = useState({})
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState({
    driver: false,
    vehicle: false
  })
  const [newDriver, setNewDriver] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    license_number: '',
    status: true,
    location_id: null
  })

  const [newVehicle, setNewVehicle] = useState({
    make: '',
    model: '',
    year: '',
    color: '',
    license_plate: '',
    vin: '',
    type: 'car',
    driver_id: null,
    status: true,
    insurance_expiry_date: '',
    road_worthiness_expiry_date: ''
  })

  // Transform API data to include key for table
  const drivers = driversData?.map(driver => ({
    ...driver,
    key: driver?.id.toString()
  })) || []

  const vehicles = vehiclesData?.map(vehicle => ({
    ...vehicle,
    key: vehicle.id.toString()
  })) || []

  const handleEditDriver = (record) => {
    setEditingDriver(record)
    setNewDriver({
      first_name: record?.first_name,
      last_name: record?.last_name,
      phone: record?.phone,
      email: record?.email || '',
      license_number: record?.license_number || '',
      status: record?.status,
      location_id: record?.location_id,
    })
    setShowEditDriverModal(true)
  }

  const handleToggleStatus = async (record) => {
    try {
      await toggleDriverStatus(record?.id).unwrap()
      message.success(`Driver ${record?.status ? 'deactivated' : 'activated'} successfully`)
    } catch (error) {
      message.error('Failed to update driver status')
    }
  }


  const driverColumns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => `${record?.first_name} ${record?.last_name}`,
    },
    {
      title: 'Phone Number',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Branch',
      dataIndex: 'location_name',
      key: 'branch',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <StatusPill status={status ? 'Active' : 'Inactive'} />
    }
  ]
  const vehicleColumns = [
    {
      title: 'Make',
      dataIndex: 'make',
      key: 'make',
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'License Plate',
      dataIndex: 'license_plate',
      key: 'license_plate',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <StatusPill status={status ? 'Active' : 'Inactive'} />
    }
  ]

  const handleAddDriver = async () => {
    if (newDriver?.first_name && newDriver?.last_name && newDriver?.phone && newDriver?.email && newDriver?.license_number && newDriver?.location_id) {
      try {
        await addDriver(newDriver).unwrap()
        message.success('Driver added successfully')
        setNewDriver({
          first_name: '',
          last_name: '',
          phone: '',
          email: '',
          license_number: '',
          status: true,
          location_id: 0
        })
        setShowAddDriverModal(false)
      } catch (error) {
        message.error('Failed to add driver')
      }
    } else {
      message.error('Please fill in all required fields including location')
    }
  }

  const handleUpdateDriver = async () => {
    if (newDriver?.first_name && newDriver?.last_name && newDriver?.phone && newDriver?.email && newDriver?.license_number && newDriver?.location_id) {
      try {
        await updateDriver({ id: editingDriver?.id, requestData: newDriver }).unwrap()
        message.success('Driver updated successfully')
        setShowEditDriverModal(false)
        setEditingDriver(null)
        setNewDriver({
          first_name: '',
          last_name: '',
          phone: '',
          email: '',
          license_number: '',
          status: true,
          location_id: 0
        })
      } catch (error) {
        message.error('Failed to update driver')
      }
    } else {
      message.error('Please fill in all required fields including location')
    }
  }


  const handleOpenDeleteDriverModal = (record) => {
    // Implement delete functionality if needed
    setNewDriver(record)
    setIsDeleteModalVisible({ driver: true, vehicle: false })
  }
  const handleCloseDeleteDriverModal = () => {
    setIsDeleteModalVisible({ driver: false, vehicle: false })
    setNewDriver(null)
  }
  const handleDeleteDriver = async () => {
    try {
      await deleteDriver(newDriver?.id).unwrap()
      message.success('Driver deleted successfully')
      setIsDeleteModalVisible({ driver: false, vehicle: false })
    } catch (error) {
      message.error('Failed to delete driver')
    }
  }

  const handleSelectDrivers = (selectedRowKeys, selectedRows) => {
    setSelectedDrivers(selectedRowKeys)
  }

  const handleSelectVehicles = (selectedRowKeys, selectedRows) => {
    setSelectedVehicles(selectedRowKeys)
  }

  const handleViewDriver = (record) => {
    setViewingDriver(record)
    setShowViewDriverModal(true)
  }

  const handleViewVehicle = (record) => {
    setViewingVehicle(record)
    setShowViewVehicleModal(true)
  }

  const handleEditVehicle = (record) => {
    setEditingVehicle(record)
    setNewVehicle({
      make: record?.make,
      model: record?.model,
      year: record?.year,
      color: record?.color,
      license_plate: record?.license_plate,
      vin: record?.vin,
      type: record?.type,
      driver_id: record?.driver_id,
      status: record?.status,
      insurance_expiry_date: record?.insurance_expiry_date || '',
      road_worthiness_expiry_date: record?.road_worthiness_expiry_date || ''
    })
    setShowEditVehicleModal(true)
  }

  const handleAddVehicle = async () => {
    if (newVehicle?.make && newVehicle?.model && newVehicle?.license_plate) {
      try {
        await addVehicle(newVehicle).unwrap()
        message.success('Vehicle added successfully')
        setNewVehicle({
          make: '',
          model: '',
          year: '',
          color: '',
          license_plate: '',
          vin: '',
          type: 'car',
          driver_id: null,
          status: true,
          insurance_expiry_date: '',
          road_worthiness_expiry_date: ''
        })
        setShowAddVehicleModal(false)
      } catch (error) {
        message.error('Failed to add vehicle')
      
      }
    }
  }

  const handleUpdateVehicle = async () => {
    if (newVehicle?.make && newVehicle?.model && newVehicle?.license_plate) {
      try {
        await updateVehicle({ id: editingVehicle.id, requestData: newVehicle }).unwrap()
        message.success('Vehicle updated successfully')
        setShowEditVehicleModal(false)
        setEditingVehicle(null)
        setNewVehicle({
          make: '',
          model: '',
          year: '',
          color: '',
          license_plate: '',
          vin: '',
          type: 'car',
          driver_id: null,
          status: true,
          insurance_expiry_date: '',
          road_worthiness_expiry_date: ''
        })
      } catch (error) {
        message.error('Failed to update vehicle')
        console.error('Update vehicle error:', error)
      }
    }
  }

  const handleOpenDeleteVehicleModal = (record) => {
    setNewVehicle(record)
    setIsDeleteModalVisible({ driver: false, vehicle: true })
  }
  const handleCloseDeleteVehicleModal = () => {
    setIsDeleteModalVisible({ driver: false, vehicle: false })
    setNewVehicle(null)
  }

  const handleConfirmDeleteVehicle = async () => {
    try {
      await deleteVehicle(newVehicle?.id).unwrap()
      message.success('Vehicle deleted successfully')
      setIsDeleteModalVisible({ driver: false, vehicle: false })
      setNewVehicle(null)
    } catch (error) {
      message.error('Failed to delete vehicle')
   
    }
  }


  const handleToggleVehicleStatus = (record) => {
    // Implement toggle status functionality if needed
    
  }

  // Filter configuration for drivers
  const driverFilterConfig = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      placeholder: 'All Status',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
      ]
    },
    {
      key: 'name',
      label: 'Name',
      type: 'input',
      placeholder: 'Search by name'
    }
  ]

  // Filter configuration for vehicles
  const vehicleFilterConfig = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      placeholder: 'All Status',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
      ]
    },
    {
      key: 'type',
      label: 'Type',
      type: 'select',
      placeholder: 'All Types',
      options: [
        { value: 'car', label: 'Car' },
        { value: 'truck', label: 'Truck' },
        { value: 'van', label: 'Van' },
        { value: 'motorcycle', label: 'Motorcycle' }
      ]
    },
    {
      key: 'make',
      label: 'Make',
      type: 'input',
      placeholder: 'Search by make'
    }
  ]

  // Apply filters for drivers
  const handleApplyDriverFilters = (filterValues) => {
    setFilters(filterValues)
    let filtered = [...drivers]

    if (filterValues.status) {
      const isActive = filterValues.status === 'active'
      filtered = filtered.filter(driver => driver?.status === isActive)
    }

    if (filterValues.name) {
      const searchTerm = filterValues.name.toLowerCase()
      filtered = filtered.filter(driver =>
        `${driver?.first_name} ${driver?.last_name}`.toLowerCase().includes(searchTerm)
      )
    }

    setFilteredDrivers(filtered)
  }

  // Apply filters for vehicles
  const handleApplyVehicleFilters = (filterValues) => {
    setVehicleFilters(filterValues)
    let filtered = [...vehicles]

    if (filterValues.status) {
      const isActive = filterValues.status === 'active'
      filtered = filtered.filter(vehicle => vehicle.status === isActive)
    }

    if (filterValues.type) {
      filtered = filtered.filter(vehicle => vehicle.type === filterValues.type)
    }

    if (filterValues.make) {
      const searchTerm = filterValues.make.toLowerCase()
      filtered = filtered.filter(vehicle =>
        vehicle.make.toLowerCase().includes(searchTerm)
      )
    }

    setFilteredVehicles(filtered)
  }

  // Reset filters
  const handleResetDriverFilters = () => {
    setFilters({})
    setFilteredDrivers([])
  }

  const handleResetVehicleFilters = () => {
    setVehicleFilters({})
    setFilteredVehicles([])
  }

  // Get display data (filtered or all)
  const displayDriverData = filteredDrivers.length > 0 ? filteredDrivers : drivers
  const displayVehicleData = filteredVehicles.length > 0 ? filteredVehicles : vehicles


  return (
    <div className="flex h-full bg-white">
      <div className='flex flex-col w-full'>
        <div className="flex h-full">
          {/* Left Section - Title and Description */}
          <div className="w-80 p-6 flex flex-col justify-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Drivers</h2>
            <p className="text-gray-600">Add your drivers for delivery</p>
          </div>

          {/* Right Section - Table */}
          <div className="flex-1 p-6">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              {/* Table Header */}
              <div className="flex justify-between items-center p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800">SFS Drivers</h3>
                <Space>
                  {/* <FilterDropdown
                    filterConfig={driverFilterConfig}
                    onApply={handleApplyDriverFilters}
                    onReset={handleResetDriverFilters}
                    buttonText="Filter"
                    buttonType="default"
                  /> */}
                  <Button
                    type="primary"
                    onClick={() => setShowAddDriverModal(true)}
                    className="bg-orange-500 hover:bg-orange-600 border-orange-500 text-white"
                    loading={addingDriver}
                  >
                    + Add
                  </Button>
                </Space>
              </div>

              {/* Table Content */}
              <CustomSimpleTable
                data={displayDriverData}
                columns={driverColumns}
                onEdit={handleEditDriver}
                onDelete={handleOpenDeleteDriverModal}
                onToggleStatus={handleToggleStatus}
                onSelect={handleSelectDrivers}
                onView={handleViewDriver}
                selectedRowKeys={selectedDrivers}
                showCheckbox={true}
                showActions={true}
                loading={driversLoading}
              />
            </div>
          </div>



        </div>
        <Divider />
        <div className="flex h-full">
          {/* Left Section - Title and Description */}
          <div className="w-80 p-6 flex flex-col justify-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Vehicles</h2>
            <p className="text-gray-600">Add your vehicles for delivery</p>
          </div>

          {/* Right Section - Table */}
          <div className="flex-1 p-6">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              {/* Table Header */}
              <div className="flex justify-between items-center p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800">SFS Vehicles</h3>
                <Space>
                  {/* <FilterDropdown
                    filterConfig={vehicleFilterConfig}
                    onApply={handleApplyVehicleFilters}
                    onReset={handleResetVehicleFilters}
                    buttonText="Filter"
                    buttonType="default"
                  /> */}
                  <Button
                    type="primary"
                    onClick={() => setShowAddVehicleModal(true)}
                    className="bg-orange-500 hover:bg-orange-600 border-orange-500 text-white"
                    loading={addingVehicle}
                  >
                    + Add
                  </Button>
                </Space>
              </div>

              {/* Table Content */}
              <CustomSimpleTable
                data={displayVehicleData}
                columns={vehicleColumns}
                onEdit={handleEditVehicle}
                onDelete={handleOpenDeleteVehicleModal}
                onToggleStatus={handleToggleVehicleStatus}
                onSelect={handleSelectVehicles}
                onView={handleViewVehicle}
                selectedRowKeys={selectedVehicles}
                showCheckbox={true}
                showActions={true}
                loading={vehiclesLoading}
              />
            </div>
          </div>


        </div>
      </div>
      {/* Add Driver Modal */}
      <CustomModal
        isOpen={showAddDriverModal}
        onClose={() => setShowAddDriverModal(false)}
        title="Add New Driver"
        subtitle="Enter driver information"
        primaryActionText="Add Driver"
        onPrimaryAction={handleAddDriver}
        secondaryActionText="Cancel"
        onSecondaryAction={() => setShowAddDriverModal(false)}
        width={600}
        rightOffset={0}
        loading={addingDriver}
      >
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
              <Input
                value={newDriver?.first_name}
                onChange={(e) => setNewDriver({ ...newDriver, first_name: e.target.value })}
                placeholder="Enter first name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
              <Input
                value={newDriver?.last_name}
                onChange={(e) => setNewDriver({ ...newDriver, last_name: e.target.value })}
                placeholder="Enter last name"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
              <Input
                value={newDriver?.phone}
                onChange={(e) => setNewDriver({ ...newDriver, phone: e.target.value })}
                placeholder="+2348012345678"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
              <Input
                value={newDriver?.email}
                onChange={(e) => setNewDriver({ ...newDriver, email: e.target.value })}
                placeholder="johndoe@example.com"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">License Number *</label>
              <Input
                value={newDriver?.license_number}
                onChange={(e) => setNewDriver({ ...newDriver, license_number: e.target.value })}
                placeholder="LIC-123456"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Location *</label>
              <Select
                value={newDriver?.location_id}
                onChange={(value) => setNewDriver({ ...newDriver, location_id: value })}
                className="w-full"
                placeholder="Select location"
                loading={locationsLoading}
              >
                {locationsData?.map(location => (
                  <Select.Option key={location.id} value={location.id}>
                    {location.name}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <Select
              value={newDriver?.status}
              onChange={(value) => setNewDriver({ ...newDriver, status: value })}
              className="w-full"
            >
              <Select.Option value={true}>Active</Select.Option>
              <Select.Option value={false}>Inactive</Select.Option>
            </Select>
          </div>
        </div>
      </CustomModal>

      {/* Edit Driver Modal */}
      <CustomModal
        isOpen={showEditDriverModal}
        onClose={() => setShowEditDriverModal(false)}
        title="Edit Driver"
        subtitle="Update driver information"
        primaryActionText="Update Driver"
        onPrimaryAction={handleUpdateDriver}
        secondaryActionText="Cancel"
        onSecondaryAction={() => setShowEditDriverModal(false)}
        width={600}
        rightOffset={0}
        loading={updatingDriver}
      >
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
              <Input
                value={newDriver?.first_name}
                onChange={(e) => setNewDriver({ ...newDriver, first_name: e.target.value })}
                placeholder="Enter first name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
              <Input
                value={newDriver?.last_name}
                onChange={(e) => setNewDriver({ ...newDriver, last_name: e.target.value })}
                placeholder="Enter last name"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
              <Input
                value={newDriver?.phone}
                onChange={(e) => setNewDriver({ ...newDriver, phone: e.target.value })}
                placeholder="+2348012345678"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
              <Input
                value={newDriver?.email}
                onChange={(e) => setNewDriver({ ...newDriver, email: e.target.value })}
                placeholder="johndoe@example.com"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">License Number *</label>
              <Input
                value={newDriver?.license_number}
                onChange={(e) => setNewDriver({ ...newDriver, license_number: e.target.value })}
                placeholder="LIC-123456"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Location *</label>
              <Select
                value={newDriver?.location_id}
                onChange={(value) => setNewDriver({ ...newDriver, location_id: value })}
                className="w-full"
                placeholder="Select location"
                loading={locationsLoading}
              >
                {locationsData?.map(location => (
                  <Select.Option key={location.id} value={location.id}>
                    {location.name}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <Select
              value={newDriver?.status}
              onChange={(value) => setNewDriver({ ...newDriver, status: value })}
              className="w-full"
            >
              <Select.Option value={true}>Active</Select.Option>
              <Select.Option value={false}>Inactive</Select.Option>
            </Select>
          </div>
        </div>
      </CustomModal>

      {/* View Driver Modal */}
      <CustomModal
        isOpen={showViewDriverModal}
        onClose={() => setShowViewDriverModal(false)}
        title="Driver Details"
        subtitle="View driver information"
        primaryActionText="Close"
        onPrimaryAction={() => setShowViewDriverModal(false)}
        secondaryActionText=""
        hideSecondaryAction
        width={600}
        rightOffset={0}
        showSecondary={false}
      >
        {viewingDriver && (
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                <div className="p-2 bg-gray-50 rounded border">{viewingDriver?.first_name}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                <div className="p-2 bg-gray-50 rounded border">{viewingDriver?.last_name}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                <div className="p-2 bg-gray-50 rounded border">{viewingDriver?.phone}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <div className="p-2 bg-gray-50 rounded border">{viewingDriver?.email}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">License Number</label>
                <div className="p-2 bg-gray-50 rounded border">{viewingDriver?.license_number}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                <div className="p-2 bg-gray-50 rounded border">
                  {locationsData?.find(loc => loc.id === viewingDriver?.location_id)?.name || 'N/A'}
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <div className="p-2 bg-gray-50 rounded border">
                <StatusPill status={viewingDriver?.status ? 'Active' : 'Inactive'} />
              </div>
            </div>
          </div>
        )}
      </CustomModal>

      {/* Add Vehicle Modal */}
      <CustomModal
        isOpen={showAddVehicleModal}
        onClose={() => setShowAddVehicleModal(false)}
        title="Add New Vehicle"
        subtitle="Enter vehicle information"
        primaryActionText="Add Vehicle"
        onPrimaryAction={handleAddVehicle}
        secondaryActionText="Cancel"
        onSecondaryAction={() => setShowAddVehicleModal(false)}
        width={700}
        rightOffset={0}
        loading={addingVehicle}
      >
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Make *</label>
              <Input
                value={newVehicle?.make}
                onChange={(e) => setNewVehicle({ ...newVehicle, make: e.target.value })}
                placeholder="Toyota"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Model *</label>
              <Input
                value={newVehicle?.model}
                onChange={(e) => setNewVehicle({ ...newVehicle, model: e.target.value })}
                placeholder="Corolla"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
              <Input
                value={newVehicle?.year}
                onChange={(e) => setNewVehicle({ ...newVehicle, year: e.target.value })}
                placeholder="2020"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
              <Input
                value={newVehicle?.color}
                onChange={(e) => setNewVehicle({ ...newVehicle, color: e.target.value })}
                placeholder="Black"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">License Plate *</label>
              <Input
                value={newVehicle?.license_plate}
                onChange={(e) => setNewVehicle({ ...newVehicle, license_plate: e.target.value })}
                placeholder="ABC123XYZ"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">VIN</label>
              <Input
                value={newVehicle?.vin}
                onChange={(e) => setNewVehicle({ ...newVehicle, vin: e.target.value })}
                placeholder="1HGCM82633A004353"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
              <Select
                value={newVehicle?.type}
                onChange={(value) => setNewVehicle({ ...newVehicle, type: value })}
                className="w-full"
              >
                <Select.Option value="car">Car</Select.Option>
                <Select.Option value="truck">Truck</Select.Option>
                <Select.Option value="van">Van</Select.Option>
                <Select.Option value="motorcycle">Motorcycle</Select.Option>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Driver</label>
              <Select
                value={newVehicle?.driver_id}
                onChange={(value) => setNewVehicle({ ...newVehicle, driver_id: value })}
                className="w-full"
                placeholder="Select driver"
                allowClear
              >
                {drivers.map(driver => (
                  <Select.Option key={driver?.id} value={driver?.id}>
                    {driver?.first_name} {driver?.last_name}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Insurance Expiry Date</label>
              <Input
                type="date"
                value={newVehicle?.insurance_expiry_date}
                onChange={(e) => setNewVehicle({ ...newVehicle, insurance_expiry_date: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Road Worthiness Expiry Date</label>
              <Input
                type="date"
                value={newVehicle?.road_worthiness_expiry_date}
                onChange={(e) => setNewVehicle({ ...newVehicle, road_worthiness_expiry_date: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <Select
              value={newVehicle?.status}
              onChange={(value) => setNewVehicle({ ...newVehicle, status: value })}
              className="w-full"
            >
              <Select.Option value={true}>Active</Select.Option>
              <Select.Option value={false}>Inactive</Select.Option>
            </Select>
          </div>
        </div>
      </CustomModal>

      {/* Edit Vehicle Modal */}
      <CustomModal
        isOpen={showEditVehicleModal}
        onClose={() => setShowEditVehicleModal(false)}
        title="Edit Vehicle"
        subtitle="Update vehicle information"
        primaryActionText="Update Vehicle"
        onPrimaryAction={handleUpdateVehicle}
        secondaryActionText="Cancel"
        onSecondaryAction={() => setShowEditVehicleModal(false)}
        width={700}
        rightOffset={0}
        loading={updatingVehicle}
      >
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Make *</label>
              <Input
                value={newVehicle?.make}
                onChange={(e) => setNewVehicle({ ...newVehicle, make: e.target.value })}
                placeholder="Toyota"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Model *</label>
              <Input
                value={newVehicle?.model}
                onChange={(e) => setNewVehicle({ ...newVehicle, model: e.target.value })}
                placeholder="Corolla"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
              <Input
                value={newVehicle?.year}
                onChange={(e) => setNewVehicle({ ...newVehicle, year: e.target.value })}
                placeholder="2020"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
              <Input
                value={newVehicle?.color}
                onChange={(e) => setNewVehicle({ ...newVehicle, color: e.target.value })}
                placeholder="Black"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">License Plate *</label>
              <Input
                value={newVehicle?.license_plate}
                onChange={(e) => setNewVehicle({ ...newVehicle, license_plate: e.target.value })}
                placeholder="ABC123XYZ"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">VIN</label>
              <Input
                value={newVehicle?.vin}
                onChange={(e) => setNewVehicle({ ...newVehicle, vin: e.target.value })}
                placeholder="1HGCM82633A004353"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
              <Select
                value={newVehicle?.type}
                onChange={(value) => setNewVehicle({ ...newVehicle, type: value })}
                className="w-full"
              >
                <Select.Option value="car">Car</Select.Option>
                <Select.Option value="truck">Truck</Select.Option>
                <Select.Option value="van">Van</Select.Option>
                <Select.Option value="motorcycle">Motorcycle</Select.Option>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Driver</label>
              <Select
                value={newVehicle?.driver_id}
                onChange={(value) => setNewVehicle({ ...newVehicle, driver_id: value })}
                className="w-full"
                placeholder="Select driver"
                allowClear
              >
                {drivers.map(driver => (
                  <Select.Option key={driver?.id} value={driver?.id}>
                    {driver?.first_name} {driver?.last_name}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Insurance Expiry Date</label>
              <Input
                type="date"
                value={newVehicle?.insurance_expiry_date}
                onChange={(e) => setNewVehicle({ ...newVehicle, insurance_expiry_date: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Road Worthiness Expiry Date</label>
              <Input
                type="date"
                value={newVehicle?.road_worthiness_expiry_date}
                onChange={(e) => setNewVehicle({ ...newVehicle, road_worthiness_expiry_date: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <Select
              value={newVehicle?.status}
              onChange={(value) => setNewVehicle({ ...newVehicle, status: value })}
              className="w-full"
            >
              <Select.Option value={true}>Active</Select.Option>
              <Select.Option value={false}>Inactive</Select.Option>
            </Select>
          </div>
        </div>
      </CustomModal>

      {/* View Vehicle Modal */}
      <CustomModal
        isOpen={showViewVehicleModal}
        onClose={() => setShowViewVehicleModal(false)}
        title="Vehicle Details"
        subtitle="View vehicle information"
        primaryActionText="Close"
        onPrimaryAction={() => setShowViewVehicleModal(false)}
        secondaryActionText=""
        onSecondaryAction={() => {}}
        width={700}
        rightOffset={0}
        showSecondary={false}
             hideSecondaryAction
      >
        {viewingVehicle && (
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Make</label>
                <div className="p-2 bg-gray-50 rounded border">{viewingVehicle?.make}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                <div className="p-2 bg-gray-50 rounded border">{viewingVehicle?.model}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                <div className="p-2 bg-gray-50 rounded border">{viewingVehicle?.year}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                <div className="p-2 bg-gray-50 rounded border">{viewingVehicle?.color}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">License Plate</label>
                <div className="p-2 bg-gray-50 rounded border">{viewingVehicle?.license_plate}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">VIN</label>
                <div className="p-2 bg-gray-50 rounded border">{viewingVehicle?.vin}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                <div className="p-2 bg-gray-50 rounded border capitalize">{viewingVehicle?.type}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <div className="p-2 bg-gray-50 rounded border">
                  <StatusPill status={viewingVehicle?.status ? 'Active' : 'Inactive'} />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Insurance Expiry Date</label>
                <div className="p-2 bg-gray-50 rounded border">{viewingVehicle?.insurance_expiry_date || 'N/A'}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Road Worthiness Expiry Date</label>
                <div className="p-2 bg-gray-50 rounded border">{viewingVehicle?.road_worthiness_expiry_date || 'N/A'}</div>
              </div>
            </div>
            {viewingVehicle?.driver && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Assigned Driver</label>
                <div className="p-2 bg-gray-50 rounded border">
                  {viewingVehicle?.driver?.first_name} {viewingVehicle?.driver?.last_name}
                </div>
              </div>
            )}
          </div>
        )}
      </CustomModal>
      <ConfirmModal
        title={`Delete ${isDeleteModalVisible?.driver ? newDriver?.first_name + ' ' + newDriver?.last_name : newVehicle?.make} ${newVehicle?.model}`}
        isOpen={isDeleteModalVisible?.driver || isDeleteModalVisible?.vehicle}
        handleClose={isDeleteModalVisible?.driver ? handleCloseDeleteDriverModal : handleCloseDeleteVehicleModal}
        onDelete={isDeleteModalVisible?.driver ? handleDeleteDriver : handleConfirmDeleteVehicle}
        content={
          isDeleteModalVisible?.driver
            ? <>driver <b>"{newDriver?.first_name} {newDriver?.last_name}"</b></>
            : <>vehicle <b>"{newVehicle?.make} {newVehicle?.model}"</b></>
        }
        loading={deletingVehicle || deletingDriver}
      />
    </div>
  )
}

export default Drivers